import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts'
import { getCapsulas, getPersonasMant, deleteCapsula, exportCapsulasExcel } from '../../api/client'
import { hoyLocal } from '../partes/fechas'
import { useReloj } from './useReloj'
import { duracion, horas, rangos, resumir, segundosVivos } from './tiempo'
import ListaCapsulas from './ListaCapsulas'
import PersonaTrabajo, { Cifra, Barra } from './PersonaTrabajo'
import { usePuedeEditarMantenimiento } from '../../utils/mantenimiento'

// Reports: the time worked over a period -- per person and per parte, normal
// and overtime -- with the full record and an Excel download.
export default function ReportesCapsulas() {
  const { t } = useTranslation('engineering')
  const navigate = useNavigate()
  const puedeEditar = usePuedeEditarMantenimiento()
  const ahora = useReloj()
  const hoy = hoyLocal()
  const [filtro, setFiltro] = useState(() => ({ ...rangos(hoy).mes, persona_id: '' }))
  const [personas, setPersonas] = useState([])
  const [caps, setCaps] = useState(null)
  const [recibido, setRecibido] = useState(0)
  const [error, setError] = useState('')
  const [verDetalle, setVerDetalle] = useState(false)
  const [viendo, setViendo] = useState(null)
  const [exportando, setExportando] = useState(false)

  useEffect(() => { getPersonasMant().then(setPersonas).catch(() => {}) }, [])
  const cargar = useCallback(() => {
    if (!filtro.desde || !filtro.hasta || filtro.desde > filtro.hasta) return
    getCapsulas({ desde: filtro.desde, hasta: filtro.hasta, persona_id: filtro.persona_id || undefined })
      .then(cs => { setCaps(cs); setRecibido(Date.now()); setError('') })
      .catch(() => setError(t('capsulas.cargaFallo')))
  }, [filtro, t])
  useEffect(() => { cargar() }, [cargar])

  const vivas = (caps || []).map(c => ({ ...c, segundos: segundosVivos(c, recibido, ahora) }))
  const r = resumir(vivas)
  const grafico = r.porPersona.map(p => ({ nombre: p.nombre, normal: +(p.normal / 3600).toFixed(2), extra: +(p.extra / 3600).toFixed(2) }))
  const rs = rangos(hoy)
  const rangoActivo = Object.keys(rs).find(k => rs[k].desde === filtro.desde && rs[k].hasta === filtro.hasta)

  const descargar = async () => {
    setExportando(true)
    try { await exportCapsulasExcel({ desde: filtro.desde, hasta: filtro.hasta, persona_id: filtro.persona_id ? Number(filtro.persona_id) : null }) }
    catch { setError(t('unidades.filtros.exportFailed')) }
    setExportando(false)
  }
  const borrar = async (c) => {
    if (!window.confirm(t('capsulas.borrarConfirm', { persona: c.persona_nombre, duracion: duracion(c.segundos) }))) return
    try { await deleteCapsula(c.id) } finally { cargar() }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          {Object.keys(rs).map(k => (
            <button key={k} type="button" onClick={() => setFiltro(f => ({ ...f, ...rs[k] }))}
              className={`px-3 py-1 rounded-md text-sm font-medium ${rangoActivo === k ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
              {t(`capsulas.rangos.${k}`)}
            </button>
          ))}
        </div>
        <label className="text-xs text-gray-500">{t('capsulas.registrar.desde')}
          <input type="date" value={filtro.desde} max={filtro.hasta} onChange={e => setFiltro(f => ({ ...f, desde: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900" />
        </label>
        <label className="text-xs text-gray-500">{t('capsulas.registrar.hasta')}
          <input type="date" value={filtro.hasta} min={filtro.desde} onChange={e => setFiltro(f => ({ ...f, hasta: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900" />
        </label>
        <label className="text-xs text-gray-500">{t('capsulas.persona')}
          <select value={filtro.persona_id} onChange={e => setFiltro(f => ({ ...f, persona_id: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900">
            <option value="">{t('capsulas.todas')}</option>
            {personas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        </label>
        <button type="button" onClick={descargar} disabled={exportando || !caps?.length}
          className="ml-auto inline-flex items-center gap-1.5 bg-gray-900 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
          {exportando ? t('unidades.filtros.exporting') : t('unidades.filtros.export')}
        </button>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      {!caps ? <p className="text-gray-400 text-sm">{t('unidades.loading')}</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Cifra etiqueta={t('capsulas.horasTotales')} valor={`${horas(r.total.seg)} h`} fuerte />
            <Cifra etiqueta={t('capsulas.normales')} valor={`${horas(r.total.normal)} h`} />
            <Cifra etiqueta={t('capsulas.extras')} valor={`${horas(r.total.extra)} h`} ambar={r.total.extra > 0} />
            <Cifra etiqueta={t('capsulas.partesTrabajados')} valor={r.porParte.length} />
            <Cifra etiqueta={t('capsulas.capsulas')} valor={r.total.n} />
          </div>

          {grafico.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('capsulas.horasPorPersona')}</h3>
              <ResponsiveContainer width="100%" height={Math.max(160, grafico.length * 38)}>
                <BarChart data={grafico} layout="vertical" margin={{ left: 10, right: 20, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" unit=" h" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="nombre" width={90} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v, n) => [`${String(v).replace('.', ',')} h`, n === 'normal' ? t('capsulas.normales') : t('capsulas.extras')]} />
                  <Legend formatter={n => (n === 'normal' ? t('capsulas.normales') : t('capsulas.extras'))} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="normal" stackId="h" fill="#34d399" />
                  <Bar dataKey="extra" stackId="h" fill="#fbbf24" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-2 items-start">
            <Tabla titulo={t('capsulas.porPersona')} filas={r.porPersona} t={t}
              clave={p => p.persona_id} onClick={p => setViendo(p.persona_id)}
              nombre={p => p.nombre} detalle={p => t('capsulas.nPartes', { n: p.partes })} />
            <Tabla titulo={t('capsulas.porParte')} filas={r.porParte} t={t}
              clave={p => p.parte_id} onClick={p => navigate(`/operations/partes?parte=${p.parte_id}`)}
              nombre={p => <>#{p.numero} <span className="font-mono text-gray-500 font-normal">{p.unidad}</span></>}
              detalle={p => `${p.nombre} · ${p.personas.join(', ')}`} />
          </div>

          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <button type="button" onClick={() => setVerDetalle(v => !v)} className="text-sm font-semibold text-gray-900">
              {verDetalle ? '▾' : '▸'} {t('capsulas.detalle', { n: caps.length })}
            </button>
            {verDetalle && (
              <div className="mt-3">
                <ListaCapsulas caps={caps} recibido={recibido} ahora={ahora}
                  onPersona={setViendo} onParte={id => navigate(`/operations/partes?parte=${id}`)} onBorrar={puedeEditar ? borrar : undefined} />
              </div>
            )}
          </section>
        </>
      )}
      {viendo != null && <PersonaTrabajo key={viendo} personaId={viendo} onClose={() => setViendo(null)} />}
    </div>
  )
}

function Tabla({ titulo, filas, clave, nombre, detalle, onClick, t }) {
  const max = filas[0]?.seg || 0
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{titulo}</h3>
      {!filas.length ? <p className="text-sm text-gray-400">{t('capsulas.sinCapsulas')}</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-100">
              <th className="text-left font-medium py-1.5"></th>
              <th className="text-right font-medium py-1.5">{t('capsulas.normales')}</th>
              <th className="text-right font-medium py-1.5">{t('capsulas.extras')}</th>
              <th className="text-right font-medium py-1.5">{t('capsulas.total')}</th>
            </tr>
          </thead>
          <tbody>
            {filas.map(f => (
              <tr key={clave(f)} onClick={() => onClick(f)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer">
                <td className="py-1.5 pr-3">
                  <div className="font-medium text-gray-900">{nombre(f)}</div>
                  <div className="text-[11px] text-gray-500 truncate max-w-[260px]">{detalle(f)}</div>
                  <div className="mt-1"><Barra valor={f.seg} max={max} extra={f.extra} /></div>
                </td>
                <td className="py-1.5 text-right tabular-nums text-gray-600">{horas(f.normal)}</td>
                <td className={`py-1.5 text-right tabular-nums ${f.extra ? 'text-amber-700' : 'text-gray-300'}`}>{horas(f.extra)}</td>
                <td className="py-1.5 text-right tabular-nums font-semibold">{horas(f.seg)} h</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
